import React, { useCallback, useState } from 'react';
import {
  Divider,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';

import { CREATE_HUBSPOT_TICKET_MUTATION } from './operations';
import { useFeedbackPanelStyles } from './FeedbackPanel.style';
import Modal from '../../../components/Modals';
import { useMutation } from '@apollo/client';
import { useSnackBarContext } from '../../../contexts/SnackbarContext';
import { CloseButtons } from '../../Modal/CloseButtons';

export const FeedbackPanel = ({ open, onClose }) => {
  const classes = useFeedbackPanelStyles();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [createIssue] = useMutation(CREATE_HUBSPOT_TICKET_MUTATION);
  const [loading, setLoading] = useState(false);
  const { setSnack } = useSnackBarContext();

  const resetForm = () => {
    setSubject('');
    setDescription('');
    setFeedbackType('');
    setLoading(false);
  };

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    await createIssue({
      variables: {
        issueType: feedbackType,
        summary: subject,
        description: description,
      },
    });
    onClose();
    setSnack({
      open: true,
      severity: 'success',
      message: "Thanks for your feedback! We'll review it soon.",
    });
    resetForm();
  }, [feedbackType, subject, description]);

  const handleChange = (event) => {
    setFeedbackType(event.target.value);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      handleClose={handleClose}
      open={open}
      title={"We'd love your feedback"}
      titleAlignedWithClose={true}
      styles={classes}
    >
      <Divider sx={{ width: '100%' }} />
      <div className={classes.content}>
        <FormControl fullWidth={true}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Please select your issue
          </Typography>
          <RadioGroup
            row
            aria-labelledby="row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            className={classes.radioGroup}
            defaultValue={'custom'}
            onChange={handleChange}
          >
            <FormControlLabel
              value="PRODUCT_ISSUE"
              control={<Radio />}
              label="Data or functionality issue"
            />
            <FormControlLabel
              value="FEATURE_REQUEST"
              control={<Radio />}
              label="Feature request or enhancement"
            />
          </RadioGroup>
        </FormControl>
        <div className={classes.dividerWrapper}>
          <Divider className={classes.divider} />
        </div>

        <div className={classes.feedbackDetails}>
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Please provide more details
          </Typography>

          <div className={classes.inputGroup}>
            <TextField
              label="Subject"
              variant="outlined"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={classes.textField}
              size="small"
              placeholder="Brief summary of your feedback"
              slotProps={{ htmlInput: { maxLength: 50 } }}
            />
          </div>

          <div className={classes.inputGroup}>
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={classes.textField}
              size="small"
              placeholder="Please provide detailed information about your feedback"
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </div>
        </div>
      </div>
      <CloseButtons
        primaryAction={{
          onClick: handleSubmit,
          disabled: !feedbackType || !subject || !description,
          loading,
        }}
        variant="submit"
      />
    </Modal>
  );
};
